import { logRocketError } from "@/utils/logUtils";
import { allClusters, cluster0, cluster3, cluster6, errorCorrectionCoefficients, textCompactionAlphaLower, textCompactionAlphaUpper, textCompactionMixed, textCompactionPunctuation } from "./pdf417tables";
import { write1s } from "./utils";

/*

This file generates 2D Barcodes in the format PDF417.

It implements a sub-set of the PDF417 encoding to do this.

The output format is a black and white, 1-bit-per-pixel bitmap stored in a javascript byte array.

---

We need to do this because the labels sellers will place on boxes can (optionally) embed a 2D barcode which encodes the contents of the box.

For LTL/FTL shipments, 2D barcodes are extremely valuable for the convienence they provide in encoding box contents.

---

PDF417:
  best resources:
  https://www.expresscorp.com/uploads/specifications/44/USS-PDF-417.pdf (usually when I refer to the 'spec' in comments I mean this document - it's not actually the ISO standard document though)
  https://grandzebu.net/informatique/codbar-en/pdf417.htm
  https://en.wikipedia.org/wiki/PDF417
  https://web.archive.org/web/20051112052129/http://webpages.charter.net/jtlien/pdf.html
  (I have some others saved offline that I can't find the original links too also...)

  other misc:
  https://bfo.com/blog/2017/09/25/pdf417_barcodes_or_the_nice_thing_about_standards_is_there_are_so_many_to_choose_from/
  https://www.barcoderesource.com/PDF417Barcode.pdf
  https://www.silverbaytech.com/files/pdf417/pdf_api.pdf
  https://www.barcodefaq.com/2d/pdf417/#Modes
  https://download.dymo.com/UserManuals/labelwriter%20user%20guides/LWSE450_Tech_Ref/Content/PDF417%202D%20Barcode.htm

---

This is my notes on the spec itself, diluting the details from the spec docs above:

Each pdf417 barcode has between 3 and 90 stacked rows.

Each row has:
a. Leading quiet zone
b. start pattern
c. left row indicator symbol character
d. one to thirty data symbol characters
e. right row indicator symbol character
f. stop pattern
g. trailing quiet zone

The number of columns is therefore between 1..30 inclusive, not counting the start/stop pattern, leading/trailing quiet zone, or left/right row indicators.

Note that 'Columns' is not referring to pixels here, it's referring to the number of 'symbol characters'.

A symbol character is just a pattern of bars/spaces that has some meaning. The meaning or number assigned to a symbol character is called a 'codeword'.

---

quiet zones are just whitespace of minimum width of 2 modules. (a module is just a 'unit' width... the width of the smallest bar/space)

start pattern is the bar/space pattern of exactly: 8 1 1 1 1 1 1 3
stop pattern is the bar/space pattern of exactly:  7 1 1 3 1 1 1 2 1

note the extra black bar at the end of the stop pattern. it's the only symbol with 9 bars/spaces instead of 8.

both the stop and start patterns are full-height - effectively the only non-2D part of the barcode.

---

section 'd.' is where the actual data is.

a pdf417 barcode 'character' is four bars and four spaces, each from 1-6 modules in size.
characters always start with a bar and end with a space
the total 'width' of the bars and spaces must equal 17

data symbol characters are seventeen 'modules' wide, and consist of an alternating pattern of 4 bars and 4 spaces - starting with a bar. Each space/bar is from 1-6 'modules' wide. (module just means the width of the smallest bar/space, could just think of it as 'units').

each data symbol character encodes a number in the range 0..928 inclusive (base 929 encoding). The values are called 'codewords' (similar concept to a unicode codepoint).

the first data symbol character (top left) always encodes the length of the data region, including itself, but excluding error correction. The max length of a PDF417 data region is 928 (effectively 927, because the length is also encoded).

data proceeds encoded in left-to-right, top-row-to-bottom read order.

because data is in this 2d array, this means you sometimes need to pad data. You can do this with codeword 900.

in pdf417, there are three different mutually exclusive sets of ways to represent values in 0..928 range as combinations of bars and spaces. These sets are called 'clusters'. There is cluster0, cluster3, and cluster6.

The value of a symbol character within a particular cluster we'll call the 'symbol character value or codeword'.

You can determine the cluster used by a symbol by the following formula: cluster number = (b1 - b2 + b3 - b4 + 9) mod 9
where b1..b4 are the widths of the bars (not the spaces) in the symbol character.

You should only get back 0, 3 or 6 from this calculation - otherwise, something went wrong. So there is a cluster 0, cluster 3, and cluster 6.

Each row of the symbol must always use the same cluster. As you move through rows in the symbol, the cluster number increments, and rolls-over.

So, if you had a symbol with 5 rows, the clusters used by each row MUST be:

- row 1 - cluster 0
- row 2 - cluster 3
- row 3 - cluster 6
- row 4 - cluster 0
- row 5 - cluster 3

the values encoded in the data segment themselves also encode what row they lie in.

this is done to help the hardware notice when it crossed a row boundary.

---

PDF417 has 929 defined codewords within a cluster.

Values 0..899 encode 'data' according to compaction mode and GLI interpretation. (ignore the term GLI interpretation... it's not relevant for us, but technically does matter...)

NOTE(Nick): 'compaction' is the author of this spec's term for a lookup table/way to interpret the values encoded in the data section.

Values 900..928 are special purpose symbols:
- 900..902, 913, 924 - mode identifiers
- 925..927 - global label identifiers (GLIs)
- 922,923,928 - Macro PDF417 (WTF?)
- 921 - reader initialization (waht?)
- 903..912, 914, 920 - unused, reserved (LOL)

These values switch compaction mode:
- Text Compaction mode latch: 900
- byte compaction latch: 901/924, shift: 913
- numeric compaction latch: 902

Latch means switch from the current mode to the the target mode (900 switches to text, for example). This 'latches' in the sense that the switch is persisted until the next switch.

'Shift', 913, swaps from 'text compaction mode' to 'byte compaction mode' for exactly one codeword.

We don't give a shit about numeric or byte compaction. All our data is text (we could debatably use numeric or byte for certain pieces of data, but I think we want text for everything because if we encode numbers as numbers, pasting the data into a text editor wouldn't work - I can't imagine amazon's systems work like this. They probably just work with text. The amazon documents i've seen don't make this crystal clear, but it's a reasonable assumption. The format of the data they want you to encode implies a text-only encoding).

So we'll proceed assuming all data is encoded in text compaction.

Unfortunately, text compaction is the most complicated one, which has FOUR SUB MODES: "Alpha", "Lower Case", "Mixed", "Punctuation".

We *may* need to plan to use all four submodes. For a reason I don't understand yet, each sub-mode can only encode 30 characters, including 1-3 characters per submode for latching/switching between modes. No individual mode contains all the characters we need to encode. I think we should plan to just implement every sub-mode, because then we cover the full-ascii range to be safe, similar to what we do with code128.

Each sub-mode maps a value from 0..29 to an ascii character/control code or switching/latching code. A single symbol character value can (must, actually) encode two of these values at once. To determine the symbol character value that encodes the pair, use the following formula:

30 * H + L

where H is the leading character of the pair and L is the trailing character

What to do when your data isn't in pairs evenly (odd number of chars)? You have to pad it with a trailing value of 29.

You are forbidden from doing redundant sub-mode shifts - you can't submode shift consequetively with no data in betwee.

---

The first codeword within the data region is the symbol length descriptor. The mode structure applies from the second codeword onward. The alpha sub-moide of the text compaction mode, and GLI 0 interpretation, are always in effect at the start of each symbol.

There is a sample encoding algorithm in the spec doc, but it's unhelpful for our use case - it shows a way to minimize the number of codewords, but it does this by switching between the modes a bunch.
We only use one mode...

*/

export interface SingleBitPerPixelBitmap {
  width, height: number; // in pixels - also bits (1-bit per pixel)
  data: Uint8Array;      // the black/white pixel data
  // @TODO maybe we add something here that indicates padLeft/padRight # of bits?
}

export interface PDF417Symbol {
  bitmap: SingleBitPerPixelBitmap;
  recommendedWidth: number;
  recommendedHeight: number;
}

export enum PDF417Error {
  DATA_SEGMENT_TOO_LARGE = "DATA_SEGMENT_TOO_LARGE",
  NON_ASCII_SOURCE_STRING = "NON_ASCII_SOURCE_STRING",
}

export function pdf417(stringData: string, verbose: boolean = false): [PDF417Error, PDF417Symbol] {
  if (verbose) {
    console.debug("String data:\n", stringData);
  }

  // check that the string we want to encode is ASCII-clean (7-bit!), lets us assume some things below here.
  // @NOTE(nick) there are some ascii values that don't work here, like the null character. There are other
  // characters that are less-well-tested too, like vertical tab. The PDF417 spec does support these.
  // @TODO test/support those more. We don't care too much because the entire printable range is supported
  // and those are the characters we expect for things like box-content 2D barcodes.
  for (let i = 0; i < stringData.length; i++) {
    const char: number = stringData.charCodeAt(i);
    if (Number.isNaN(char) || char < 0 || char > 127) return [err(`char '${char}' is not ascii! original string data: |${stringData}|`, PDF417Error.NON_ASCII_SOURCE_STRING), null];
  }

  // encode the data string into codewords
  const dataCodewords: number[] = encodeDataStringPDF417TextCompaction(stringData);

  // in the MWS documentation, they recommend ECC level 6. This means adding 128 ECC codewords.
  // it seems very excessive for what we're trying to do, but I also understand the idea. The
  // reason why you'd choose a higher ECC level is for industries where label damage is very
  // common. Our use case is somewhat in this category. But it also limits how many SKUs you
  // can put in a single box pretty heavily. 5 is already the max recommended under 'normal'
  // circumstances, so I think it's fine to do for now (higher levels also make the barcode print
  // denser, which in my testing can cause some scanners to barf a little bit when there are 
  // also a very large number of data codewords. Counter-intuitively, less ECC may improve
  // scanability...)
  const errorCorrectionLevel = 5;
  // formula for # of codewords for a ECC level from 0..8: 2^(ECC+1):
  const numberOfErrorCorrectionCodewords: number = Math.pow(2, errorCorrectionLevel + 1); 

  // figure out dimensions (columns x rows)
  // PDF417 has some minimum and maximum dimensions. 
  // Row count must be between 3 and 90 inclusive, and columns must be between 1 and 30 inclusive (exclusing start, stop, and left/right row indicators)
  // the total number of data codewords must be at least 3 - 1 codeword indicating the length of the data segment, and a minimum of 2 ecc.
  let columns = 6;
  let rows;
  do {
    columns++;

    if (columns > 30) {
      return [err(`Failed to determine dimensions for symbol!`), null];
    }

    rows = Math.floor((dataCodewords.length + 1 + numberOfErrorCorrectionCodewords) / columns) + 1;
    if (columns * rows >= (dataCodewords.length + 1 + numberOfErrorCorrectionCodewords + columns)) {
      rows -= 1;
    }
    if (rows < 3) {
      // ensure minimum 3 rows (required by spec); (if we end up making the symbol large relative to the size of the input by doing this, all that means is we end up with more pad codewords.)
      rows = 3;
    }
  } while (rows > 90);

  let padCodewords = 0;
  {
    const n = columns * rows - numberOfErrorCorrectionCodewords;
    padCodewords = n > dataCodewords.length + 1 ? n - dataCodewords.length - 1 : 0;
  }
  // figure out how many pad-codewords we need (because it's a 2d barcode, columns x rows, but maybe our data isn't a rectangle (numerically this means the length of the data section isn't factorable)
  // const padCodewords: number = columns * rows - dataCodewords.length - numberOfErrorCorrectionCodewords - 1;
  if (verbose) {
    console.debug("#COLUMNS: ", columns, "#ROWS: ", rows, "#DATA CODEWORDS (not including first count codeword):", dataCodewords.length, "#ECC: ", numberOfErrorCorrectionCodewords, "#PAD CODEWORDS: ", padCodewords);
  }

  // at this point (yes, really, it takes this long) we know if we've overflowed the allowed # of total codewords allowed by the PDF417 spec.
  // the +1 is for the 'length' codeword. The limit of 929 comes from the fact the length codeword is itself a base 929 digit and therefore
  // cannot encode more than 929.
  if ((dataCodewords.length + numberOfErrorCorrectionCodewords + 1) > 929) {
    return [err(`Data segment too large! Source string: |${stringData}|`, PDF417Error.DATA_SEGMENT_TOO_LARGE), null];
  }

  // construct the full data-segment (minus ECC):
  const payload: number[] = [];
  payload.push(dataCodewords.length + padCodewords + 1); // write total length, including pad codewords and itself (+1), but not ECC codewords (this is encoded in the left and right row indicators)
  payload.push(...dataCodewords);
  for (let i = 0; i < padCodewords; i++) {
    payload.push(900);
  }

  // do ecc
  const errorCorrectionCodewords: number[] = new Array<number>(numberOfErrorCorrectionCodewords).fill(0);
  const payloadLengthRightNow: number = payload.length;
  for (let i = 0; i < payloadLengthRightNow; i++) {
    const temp1: number = (payload[i] + errorCorrectionCodewords[errorCorrectionCodewords.length - 1]) % 929;
    let temp2: number = 0;
    let temp3: number = 0;
    for (let j = numberOfErrorCorrectionCodewords - 1; j >= 1; j--) {
      temp2 = (temp1 * errorCorrectionCoefficients[errorCorrectionLevel][j]) % 929;
      temp3 = 929 - temp2;
      errorCorrectionCodewords[j] = (errorCorrectionCodewords[j - 1] + temp3) % 929;
    }
    temp2 = (temp1 * errorCorrectionCoefficients[errorCorrectionLevel][0]) % 929;
    temp3 = 929 - temp2;
    errorCorrectionCodewords[0] = temp3 % 929;
  }
  for (let i = numberOfErrorCorrectionCodewords - 1; i >= 0; i--) {
    if (errorCorrectionCodewords[i] !== 0) {
      errorCorrectionCodewords[i] = 929 - errorCorrectionCodewords[i];
    }
    payload.push(errorCorrectionCodewords[i]);
  }

  // 'columns' is really the number of columns in specifically the data section (which includes payload, pad, and ecc, but not left/right row indicators and start/stop symbols).
  // +4, one for start symbol, row start, row end, stop symbol. *17 for width of every symbol,
  // +1 for the single bit required for the final bar in the stop symbol.
  // +8 for quiet zones on left/right (spec minimum is 2 per side, so we double that at 4 per side - minimum. we round up to nearest multiple of 8 so rows are always byte-aligned. extra padding is allocated to the end of the row).
  const bitsPerRow = Math.ceil(((columns + 4) * 17 + 1 + 8) / 8) * 8;
  if (bitsPerRow % 8 !== 0) return [err(`bits per row is not divisible by 8! original string data: |${stringData}|`), null];

  const totalBytes = bitsPerRow * rows / 8;
  if (Math.floor(totalBytes) !== totalBytes) return [err(`total bytes is not an integer... ${totalBytes}. original string data: |${stringData}|`), null];
  
  // uint8array is 0-initialized by default. this means we only have to write 1's, or 'white'
  const a = new Uint8Array(totalBytes);

  const allClusters = [ cluster0, cluster3, cluster6 ];
  let bitsSoFar = 0;

  function writeDeadZoneAndStartSymbol(a: Uint8Array) {
    bitsSoFar = write1s(a, bitsSoFar, 4); // starting quiet zone
  
    // the start symbol itself: 8 1 1 1 1 1 1 3
    bitsSoFar += 8;
    bitsSoFar = write1s(a, bitsSoFar, 1);
    bitsSoFar += 1;
    bitsSoFar = write1s(a, bitsSoFar, 1);
    bitsSoFar += 1;
    bitsSoFar = write1s(a, bitsSoFar, 1);
    bitsSoFar += 1;
    bitsSoFar = write1s(a, bitsSoFar, 3);
  }

  function writeStopSymbolAndDeadZone(a: Uint8Array) {
    // write the stop symbol: 7 1 1 3 1 1 1 2 1
    // note the extra black bar at the end of the stop pattern. it's the only symbol with 9 bars/spaces instead of 8.
    bitsSoFar += 7;
    bitsSoFar = write1s(a, bitsSoFar, 1);
    bitsSoFar += 1;
    bitsSoFar = write1s(a, bitsSoFar, 3);
    bitsSoFar += 1;
    bitsSoFar = write1s(a, bitsSoFar, 1);
    bitsSoFar += 1;
    bitsSoFar = write1s(a, bitsSoFar, 2);
    bitsSoFar += 1; // extra black bar

    bitsSoFar = write1s(a, bitsSoFar, 4); // terminal quiet zone
  }

  // write the bitmap data:
  {
    let payloadIndex = 0;
    for (let j = 0; j < rows; j++) {
      const clusterNumber = j % 3;

      writeDeadZoneAndStartSymbol(a);

      // compute the left and right row indicator codewords: (formula given on page 4 of spec)
      let left, right;
      const xprime = 30 * Math.floor(j / 3);
      if (clusterNumber === 0) {
        left =  (xprime + Math.floor((rows - 1) / 3));
        right = (xprime + (columns - 1));
      } else if (clusterNumber === 1) {
        left =  (xprime + (errorCorrectionLevel * 3) + ((rows - 1) % 3));
        right = (xprime + Math.floor((rows - 1) / 3));
      } else {
        left =  (xprime + (columns - 1));
        right = (xprime + (errorCorrectionLevel * 3) + ((rows - 1) % 3));
      }

      if (verbose) {
        console.debug(`Payload idx: ${payloadIndex}, left: ${left}, right: ${right}, cluster#: ${clusterNumber}`);
      }

      // write the left-row-indicator
      let codewordResult = writeCodeword(a, clusterNumber, left, bitsSoFar);
      if (codewordResult.error) return [codewordResult.error, null];
      bitsSoFar = codewordResult.bitsSoFar;

      // write all data chars in this row:
      for (let i = 0; i < columns; i++) {
        codewordResult = writeCodeword(a, clusterNumber, payload[payloadIndex], bitsSoFar);
        if (codewordResult.error) return [codewordResult.error, null];
        bitsSoFar = codewordResult.bitsSoFar;
        payloadIndex += 1;
      }

      // write the right-row-indicator
      codewordResult = writeCodeword(a, clusterNumber, right, bitsSoFar);
      if (codewordResult.error) return [codewordResult.error, null];
      bitsSoFar = codewordResult.bitsSoFar;

      // the stop symbol and terminating zone...
      writeStopSymbolAndDeadZone(a);

      // because our data might not be a rectangle, we could have leftover bits... write 1s to pad
      const leftover = bitsPerRow - (bitsSoFar - bitsPerRow*j);
      bitsSoFar = write1s(a, bitsSoFar, leftover);
    }
  }

  // formula given on page 11 of spec.
  // @NOTE actually I think 'X' (scalingUnit), or the '*1' here is just an arbitrary scaling unit... representing the width of a single 'module'
  const scalingUnit: number = 1;
  const recommendedWidth: number = (17 * columns + 69) * scalingUnit + 2*4;
  const recommendedHeight: number = rows * 4 * scalingUnit + 2*4;

  const bitmap: SingleBitPerPixelBitmap = {
    data: a,
    width: bitsPerRow,
    height: rows
  };

  return [null, { bitmap, recommendedWidth, recommendedHeight }];
}

let lastErr;
function err(message: string, error?: PDF417Error): PDF417Error {
  if (message === lastErr) return error;
  lastErr = message;

  logRocketError(`Error generating PDF417 barcode: ${message}`);

  return error;
}

function clusterNumber(width1: number, width2: number, width3: number, width4: number): number {
  return (width1 - width2 + width3 - width4 + 9) % 9;
}

function writeCodeword(
  a: Uint8Array,
  clusterNumber: number,
  codeword: number,
  bitsSoFar: number
): { error?: PDF417Error, bitsSoFar: number } {
  const pattern = allClusters[clusterNumber][codeword];
  if (!pattern) {
    return { error: err(`no bar/space pattern found for cluster number ${clusterNumber} and codeword: ${codeword}`), bitsSoFar };
  };
  
  {
    // optional, verify codeword is valid for this cluster number:
    const calculatedClusterNumber = (+pattern[0] - +pattern[2] + +pattern[4] - +pattern[6] + 9) % 9;
    switch (clusterNumber) {
      case 0:
        if (calculatedClusterNumber !== 0) {
          return {
            error: err(`calculated cluster number ${calculatedClusterNumber} doesn't match provided number of ${clusterNumber*3} (actually ${clusterNumber}, but x3)`),
            bitsSoFar,
          };
        }
        break;
      case 1:
        if (calculatedClusterNumber !== 3) {
          return {
            error: err(`calculated cluster number ${calculatedClusterNumber} doesn't match provided number of ${clusterNumber*3} (actually ${clusterNumber}, but x3)`),
            bitsSoFar,
          };
        }
        break;
      case 2:
        if (calculatedClusterNumber !== 6) {
          return {
            error: err(`calculated cluster number ${calculatedClusterNumber} doesn't match provided number of ${clusterNumber*3} (actually ${clusterNumber}, but x3)`),
            bitsSoFar,
          };
        }
        break;
    }
  }

  for (let i = 0; i < pattern.length; i++) {
    if (i % 2 === 0) {
      bitsSoFar += +pattern[i];
    } else {
      bitsSoFar = write1s(a, bitsSoFar, +pattern[i]);
    }
  }
  return {
    bitsSoFar,
  };
}

function test() {
  const start = performance.now();
  // cluster tests:
  // 1. all keys, when split by character and interpreted as a number should add to 17.
  //
  // 2. all keys should be exactly 8 characters in length (representing 4 bars and 4 spaces)
  //
  // 3. each character in the key should be in the range 1..6 inclusive
  //
  // 4. keys in the cluster should be unique across all clusters, not just the cluster in which they reside
  //
  // 5. keys in the cluster should satisfy the equation: 
  //  ((b1 - b2 + b3 + b4 + 9) % 9 === cluster)
  //  where 'cluster' is 0, 3, or 6, and b1..4 are the numeric values of characters in the key at index i: 0..3 * i*2 (so, 0th index, then 2nd index, then 4th, then 6th).
  //  note that if the cluster number is calculated to be something other than 0,3,or 6, then the data is invalid.
  //
  // 6. values should be in the range 0..928 inclusive, and all values in that range should exist in the cluster exactly once.
  //
  const allKeys: Set<string> = new Set<string>();
  for (let i = 0; i < allClusters.length; i++) {
    const cluster = allClusters[i];
    if (Object.keys(cluster).length !== 929) {
      return err(`Cluster ${i*3} does not define exactly 929 elements, instead it defines: ${Object.keys(cluster).length}`)
    }
    const clusterValues: Set<number> = new Set<number>();

    for (const [value, key] of Object.entries(cluster)) { // @NOTE the reversed order of 'key'/'value', not a mistake...
      if (key.length !== 8) return err(`key in cluter${i*3}: ${key} has length not equal to 8!`);

      if (allKeys.has(key)) return err(`key in cluster${i*3}: ${key} is found either elsewhere in this cluster, or in another cluster! keys must be unique across all clusters.`);
      allKeys.add(key);

      const keyElements = key.split("").map(e => parseInt(e));
      if (keyElements.some(e => Number.isNaN(e) || e < 1 || e > 6)) return err(`key in cluster${i*3} has element that is not an integer in range 1..6, inclusive!`);
      if (keyElements.reduce((acc, curr) => acc + curr, 0) !== 17) return err(`key in cluster${i*3}: ${key} has elements (${keyElements}) that don't add to 17!`);

      const clusterN = clusterNumber(keyElements[0], keyElements[2], keyElements[4], keyElements[6]);
      if ((clusterN !== 0 && clusterN !== 3 && clusterN !== 6) || (clusterN !== (i*3))) return err(`key in cluster${i*3}: ${key} doesn't produce the correct cluster number, we got: ${clusterN}, but we expect ${i*3}`);

      if (clusterValues.has(+value)) return err(`value ${value} in cluster${i*3} associated with key: ${key} is duplicated within this cluster.`);
      clusterValues.add(+value);
    }

    for (let j = 0; j < 929; j++) {
      if (!clusterValues.has(j)) return err(`cluster${i*3} does not define value ${j}, which it must.`);
    }
  }
  // console.info(`PDF417 tests took: ${performance.now() - start}ms`);

  function randomAsciiString(maxLength: number = 2000) {
    let str = "";
    const length = Math.floor(Math.random() * (maxLength - 1)) + 1;
    for (let i = 0; i < length; i++) {
      str += String.fromCharCode(Math.floor(Math.random() * (0x7E - 0x20) + 0x20)); // range is space -> tilde, incl.
    }
    return str;
  }

  {
    const start = performance.now();
    // fuzz-test the generator (product random input strings, generate the barcode, record failures)
    const fuzzIterations = 100_000; // I've done a million, but it takes a couple minutes to complete. 100k is around 40s on my PC
    const maxStringLen = 1_000; // around >1k we start hitting the 'data segment too large' error. this depends on the selected ECC mode too though...
    let failureCount = 0;
    if (true) {
      const results = {};
      for (let i = 0; i < fuzzIterations; i++) {
        const input = randomAsciiString(maxStringLen);
        try {
          const [err, _] = pdf417(input);
          if (err) {
            if (!results[err]) results[err] = [];
            results[err].push(input);
            failureCount++;
          }
        } catch (e) {
          console.trace(e);
          if (!results[e]) results[e] = [];
          results[e].push(input);
          failureCount++;
        }
      }
      console.info(`Fuzz testing PDF417 took: ${performance.now() - start}ms`);
      console.info(`Got ${failureCount} failures and ${fuzzIterations-failureCount} successes.`)
      console.info(results);
    }
  }
}
// @TODO(nick) enable in dev/staging based on flag (optionally?)
// with the advent of the fuzz-tester, it runs very slow, and
// we don't want to enable it in prod.
// test();

// assuming |stringData| is a ASCII (7 bit only!!) string that you want to encode,
// return the 'encoded' string using PDF417's text compaction
//
// since this format does not have a single look-up table that encodes ascii for some reason,
// you have to insert these mode-switching characters into your data stream in order to encode
// all of ascii. we could maybe get away without the 'punctuation' sub-mode, but I implemented
// it for stability/reliability... it's hard to be 100% sure we could get away with not including it.
//
function encodeDataStringPDF417TextCompaction(stringData: string): number[] {

  const isAlphaUpper = (char: number): boolean => {
    return char === 32 || (char >= 65 && char <= 90);
  }
  const isAlphaLower = (char: number): boolean => {
    return char === 32 || (char >= 97 && char <= 122);
  }
  const isMixed = (char: number): boolean => {
             /* *+,-./0123456789: */             /* #$%& */             /* \t */       /* \r */       /* = */        /* ^ */
    return (char >= 42 && char <= 58) || (char >= 35 && char <= 38) || char === 9 || char === 13 || char === 61 || char === 94;
  }
  const isPunctuation = (char: number): boolean => {
                /* '()* */                       /* ,-./ */                  /* :;<>?@ */
    return (char >= 39 && char <= 42) || (char >= 44 && char <= 47) || (char >= 58 && char <= 64)
                 /* [\] */                      /* _` */                      /* {|}~ */
      || (char >= 91 && char <= 93) || (char >= 95 && char <= 96) || (char >= 123 && char <= 126)
                   /* !" */              /* $ */        /* \t */      /* \n */      /* \r */
      || (char >= 33 && char <= 34) || char === 36 || char === 9 || char === 10 || char === 13;  
  }

  const lookup = (table, n: number, stringIndex: number) => {
    const v = table[n.toString()];
    if (!v && v !== 0) return err(`key ${n} not in table ${JSON.stringify(table)}. original string data: |${stringData[stringIndex]}|`);
    return v;
  }

  const encoded: number[] = [];
  let currentSubmode: "ALPHA" | "MIXED" | "LOWER" | "PUNC" = "ALPHA"; 
  for (let i = 0; i < stringData.length;) {
    const c: number = stringData.charCodeAt(i);
    switch (currentSubmode) {
      case "ALPHA": {
        if (isAlphaUpper(c)) {
          encoded.push(lookup(textCompactionAlphaUpper, c, i));

        } else {
          if (isAlphaLower(c)) {
            currentSubmode = "LOWER";
            encoded.push(27); // lower latch
            continue;

          } else if (isMixed(c)) {
            currentSubmode = "MIXED";
            encoded.push(28); // mixed latch
            continue;

          } else {
            encoded.push(29); // punctuation shift
            encoded.push(lookup(textCompactionPunctuation, c, i));
            break;
          }
        }
      } break;

      case "LOWER": {
        if (isAlphaLower(c)) {
          encoded.push(lookup(textCompactionAlphaLower, c, i));

        } else {
          if (isAlphaUpper(c)) {
            encoded.push(27); // alpha upper shift
            encoded.push(lookup(textCompactionAlphaUpper, c, i));
            break;

          } else if (isMixed(c)) {
            currentSubmode = "MIXED";
            encoded.push(28); // mixed latch
            continue;

          } else {
            encoded.push(29); // punctuation shift
            encoded.push(lookup(textCompactionPunctuation, c, i));
            break;
          }
        }
      } break;

      case "MIXED": {
        if (isMixed(c)) {
            encoded.push(lookup(textCompactionMixed, c, i));

        } else {
          if (isAlphaUpper(c)) {
            currentSubmode = "ALPHA";
            encoded.push(28); // alpha upper latch
            continue;

          } else if (isAlphaLower(c)) {
            currentSubmode = "LOWER";
            encoded.push(27); // alpha lower latch
            continue;

          } else {
            if ((i + 1) < stringData.length) {
              const peek = stringData.charCodeAt(i + 1);
              if (isPunctuation(peek)) {
                currentSubmode = "PUNC";
                encoded.push(25); // punctuation latch
                continue;
              }
            }
            encoded.push(29); // punctuation shift
            encoded.push(lookup(textCompactionPunctuation, c, i));
          }
        }
      } break;

      case "PUNC": {
        if (isPunctuation(c)) {
          encoded.push(lookup(textCompactionPunctuation, c, i));

        } else {
          currentSubmode = "ALPHA";
          encoded.push(29); // alpha upper latch
          continue;
        }
      } break;
    }
    i++; // we increment here because we want our 'continue' cases to not increment 'i'
  }

  // we have an 'encoded' stream, but in order to figure out the 'symbol characters' (the thing that translates to the actual bar/space pattern),
  // we need to translate pairs of values in this stream to a new stream that's half the length with formula: 30*H+L where H is the first character
  // in a pair, and L is the second character in a pair.
  const output: number[] = [];
  let leadingChar: number = 0;
  for (let i = 0; i < encoded.length; i++) {
    if ((i % 2) !== 0) {
      leadingChar = leadingChar * 30 + encoded[i];
      output.push(leadingChar);

    } else {
      leadingChar = encoded[i];
    }
  }
  // handle padding - if the 'encoded' datastream has an odd number of characters, we add the trailing character + 29 (because text compaction can only encode pairs of characters)
  if ((encoded.length % 2) !== 0) {
    output.push(leadingChar * 30 + 29); // the '29' is a 'shift' character in all submodes, and has no effect here.
                                        // therefore, it's not exactly a 'pad' character, it's just a character that has no effect in this case by coincidence.
  }

  return output;
}

function toImage(bitmap: SingleBitPerPixelBitmap): HTMLImageElement {
  // the bitmap is initially in a single-bit-per-pixel format, and while there are
  // some formats which are fine with this (pdf is one, which is why we did it initially),
  // just simply displaying it in the browser as a raw image is a bit trickier.
  // here we convert it to a canvas 'ImageData' type (which is 4-bytes-per-pixel),
  // then put it into an HTMLImageElement via the canvas's 'toDataUrl' method.
  const stream = new Uint8ClampedArray(bitmap.data.length * (8 * 4));
  for (let j = 0; j < bitmap.data.length; j++) {
    for (let i = 7; i >= 0; i--) {
      const isWhite = (bitmap.data[j] & (1 << i)) & 0b1;
      if (isWhite) {
        stream[j * 4 + 0] = 255;
        stream[j * 4 + 1] = 255;
        stream[j * 4 + 2] = 255;
      }
      stream[j * 4 + 3] = 255; // alpha channel is always 1
    }
  }
  const imageData = new ImageData(stream, bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  ctx.putImageData(imageData, 0, 0);
  const image = new Image();
  image.src = canvas.toDataURL();
  return image;
}
